/*****************************************************************
 *  ZOHO BRIDGE PANEL — THE "KEYS" TAB                             *
 *  File: apps/web/src/pages/bridge/KeysTab.js                     *
 *  What it does: make a key (shown ONCE in a copy box), see every *
 *  key with when it was last used, and switch a key off with a    *
 *  reason. Switched-off keys stay on the list forever.            *
 *  Reads: GET /keys.  Writes: POST /keys, POST /keys/:id/revoke,  *
 *  only when a person presses the button.                         *
 *****************************************************************/

/*****************************************************************
 *  TABLE OF CONTENTS                                              *
 *  SECTION 1 — THE ONE-TIME COPY BOX                              *
 *  SECTION 2 — THE TAB                                            *
 *****************************************************************/

import React, { useCallback, useEffect, useState } from 'react';
import { BridgeIcon } from './bridgeIcons';
import { BRIDGE_WORDS } from './bridgeWords';
import { LoadState, ToneChip, WhyBox, formatWhen } from './bridgeParts';
import { fetchKeys, makeKey, revokeKey } from './bridgeApi';

/*****************************************************************
 *  SECTION 1 — THE ONE-TIME COPY BOX                              *
 *****************************************************************/

/* ---- KeyCopyBox({ keyText, onDone }) ---------------------------
   Shows a new key once, with a Copy button. When the person
   closes it, the key is gone from the screen for good.          */
function KeyCopyBox({ keyText, onDone }) {
  const [copyWord, setCopyWord] = useState(BRIDGE_WORDS.copyKey);

  /* ---- copyTheKey() -- put it on the clipboard, say if it worked ---- */
  async function copyTheKey() {
    try {
      await navigator.clipboard.writeText(keyText);
      setCopyWord(BRIDGE_WORDS.copied);
    } catch (copyError) {
      setCopyWord(BRIDGE_WORDS.copyFailed);
    }
  }

  return (
    <div className="zb-notice zb-notice-warn zb-keybox" role="status">
      <BridgeIcon name="key" />
      <div className="zb-keybox-inner">
        <strong>{BRIDGE_WORDS.keyShownOnce}</strong>
        <code className="zb-keytext">{keyText}</code>
        <div className="zb-row">
          <button type="button" className="zb-button zb-button-primary" onClick={copyTheKey}>
            <BridgeIcon name="copy" />
            <span>{copyWord}</span>
          </button>
          <button type="button" className="zb-button zb-button-plain" onClick={onDone}>
            Done, I saved it
          </button>
        </div>
      </div>
    </div>
  );
}

/*****************************************************************
 *  SECTION 2 — THE TAB                                            *
 *****************************************************************/

/* ---- KeysTab({ onChanged }) -- onChanged() refreshes the page counts ---- */
export default function KeysTab({ onChanged }) {
  const [keyRows, setKeyRows] = useState([]);            // every key ever made
  const [loadState, setLoadState] = useState('loading'); // loading / ready / error
  const [newKeyName, setNewKeyName] = useState('');      // the name box
  const [newKeyText, setNewKeyText] = useState('');      // shown once, then cleared
  const [answerText, setAnswerText] = useState('');      // problems from the server
  const [isMaking, setIsMaking] = useState(false);
  const [revokingId, setRevokingId] = useState('');      // the key with the why box open
  const [isRevoking, setIsRevoking] = useState(false);

  /* ---- loadKeys() ---- */
  const loadKeys = useCallback(async () => {
    setLoadState('loading');

    const answer = await fetchKeys();

    if (!answer.ok) {
      setLoadState('error');
      return;
    }

    setKeyRows(answer.data || []);
    setLoadState('ready');
  }, []);

  useEffect(() => {
    loadKeys();
  }, [loadKeys]);

  /* ---- makeTheKey(event) -- ask for a key, show it once ---- */
  async function makeTheKey(event) {
    event.preventDefault();
    setIsMaking(true);
    setAnswerText('');

    const answer = await makeKey(newKeyName.trim());

    setIsMaking(false);

    if (!answer.ok) {
      setAnswerText(answer.message || BRIDGE_WORDS.loadFailed);
      return;
    }

    setNewKeyText(answer.data.keyText);
    setNewKeyName('');
    loadKeys();
    onChanged();
  }

  /* ---- switchOff(keyId, whyText) -- revoke, then reload ---- */
  async function switchOff(keyId, whyText) {
    setIsRevoking(true);

    const answer = await revokeKey(keyId, whyText);

    setIsRevoking(false);

    if (!answer.ok) {
      setAnswerText(answer.message || BRIDGE_WORDS.loadFailed);
      return;
    }

    setRevokingId('');
    loadKeys();
    onChanged();
  }

  return (
    <section aria-labelledby="zb-tab-keys-title">
      <h2 id="zb-tab-keys-title" className="zb-visually-hidden">{BRIDGE_WORDS.tabKeys}</h2>
      <p className="zb-quiet">{BRIDGE_WORDS.keysIntro}</p>

      {newKeyText ? <KeyCopyBox keyText={newKeyText} onDone={() => setNewKeyText('')} /> : null}

      <form className="zb-filters" onSubmit={makeTheKey}>
        <label className="zb-field zb-field-grow">
          <span className="zb-label">{BRIDGE_WORDS.keyName}</span>
          <input
            className="zb-input"
            type="text"
            maxLength={120}
            value={newKeyName}
            placeholder={BRIDGE_WORDS.keyNamePlaceholder}
            onChange={(event) => setNewKeyName(event.target.value)}
          />
        </label>
        <button type="submit" className="zb-button zb-button-primary" disabled={!newKeyName.trim() || isMaking}>
          <BridgeIcon name="key" />
          <span>{isMaking ? BRIDGE_WORDS.loading : BRIDGE_WORDS.makeKey}</span>
        </button>
      </form>

      {answerText ? <div className="zb-notice zb-notice-bad" role="alert"><span>{answerText}</span></div> : null}

      {loadState !== 'ready' ? <LoadState state={loadState} onRetry={loadKeys} /> : null}
      {loadState === 'ready' && keyRows.length === 0 ? <LoadState state="empty" emptyWord={BRIDGE_WORDS.keysEmpty} /> : null}

      <ul className="zb-list">
        {keyRows.map((oneKey) => (
          <li className="zb-list-row zb-list-row-stack" key={oneKey.id}>
            <div className="zb-list-main">
              <strong>{oneKey.name}</strong>
              <code className="zb-small">{oneKey.prefix}…</code>
              {oneKey.isActive
                ? <ToneChip tone="ok" icon="check" word={BRIDGE_WORDS.keyOn} />
                : <ToneChip tone="neutral" icon="cross" word={BRIDGE_WORDS.keyOff} />}
            </div>
            <p className="zb-small">
              {BRIDGE_WORDS.lastUsed}: {oneKey.lastUsedAt ? formatWhen(oneKey.lastUsedAt) : BRIDGE_WORDS.neverUsed}
              {' · '}Made {formatWhen(oneKey.createdAt)}
              {oneKey.isActive && oneKey.expiresAt ? ' · ' + BRIDGE_WORDS.expires + ' ' + formatWhen(oneKey.expiresAt) : ''}
              {!oneKey.isActive ? ' · Off since ' + formatWhen(oneKey.revokedAt) + ' — "' + (oneKey.revokeWhy || '') + '"' : ''}
            </p>
            {oneKey.isActive && revokingId !== oneKey.id ? (
              <button type="button" className="zb-button zb-button-plain" onClick={() => setRevokingId(oneKey.id)}>
                {BRIDGE_WORDS.revoke}
              </button>
            ) : null}
            {revokingId === oneKey.id ? (
              <WhyBox
                label={BRIDGE_WORDS.revokeWhy}
                confirmWord={BRIDGE_WORDS.confirmRevoke}
                tone="danger"
                busy={isRevoking}
                onConfirm={(whyText) => switchOff(oneKey.id, whyText)}
                onCancel={() => setRevokingId('')}
              />
            ) : null}
          </li>
        ))}
      </ul>
    </section>
  );
}
